import { useMemo } from 'react';
import { useNomineeStructureCard } from '@dealstack/frontend/app/pages/Project/DealSettings/DealGeneralSettings/NomineeStructureCard/useNomineeStructureCard.ts';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { SettingsRowCard } from '@dealstack/frontend/shared/components/SettingsRowCard/SettingsRowCard.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Text } from '@mantine/core';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch';

interface Props {
	dealId?: string;
	hasNomineeStructure: boolean;
	onUpdated?: () => void;
}

// CAP-1974: Deal Settings "Nominee Structure" card (Figma 32239-184259, card node 32239:184294).
//
// The switch reflects and sets the stored `Deal.hasNomineeStructure` flag, which is the master gate
// every nominee surface reads (`DealRepository.getDealNomineeFlags`). Per-entity eligibility is set
// separately by the "Nominee structures" toggle on that entity's Securities tab.
//
// A deal that has nominee legal persons gets a footer note stating what the gate controls for them —
// switching it off only hides the split legal/beneficial views, the split holdings survive. The
// design has no footer, so a deal without nominee persons renders as the plain
// title/description/switch card.
export const NomineeStructureCard = ({ dealId, hasNomineeStructure, onUpdated }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const { nomineePersonCount, setHasNomineeStructure, canManage, isPending } =
		useNomineeStructureCard({
			dealId,
			onUpdated,
		});

	const footerNote = useMemo(() => {
		if (nomineePersonCount === 0) {
			return null;
		}
		if (hasNomineeStructure) {
			return tAdminDashboard('deal_settings.nominee_structure.hint_disable_consequence', {
				nomineeCount: nomineePersonCount,
			});
		}
		return tAdminDashboard('deal_settings.nominee_structure.hint_off_with_nominees', {
			nomineeCount: nomineePersonCount,
		});
	}, [tAdminDashboard, nomineePersonCount, hasNomineeStructure]);

	return (
		<SettingsRowCard
			data-testid={TEST_ELEMENT_IDS.DEAL_NOMINEE_STRUCTURE_CARD}
			title={tAdminDashboard('deal_settings.nominee_structure.title', {
				defaultValue: 'Nominee Structure',
			})}
			description={tAdminDashboard('deal_settings.nominee_structure.description', {
				defaultValue:
					'Enable to support separate Legal Title and Beneficial Interest ownership across securities in this deal. When activated, you will be able to mark legal persons as nominees and split interests during issuance or transfer.',
			})}
			action={
				<AdvancedSwitch
					selectedValue={hasNomineeStructure}
					disabled={!dealId || !canManage || isPending}
					onChange={(event) => setHasNomineeStructure(!!event)}
					data-testid={TEST_ELEMENT_IDS.DEAL_NOMINEE_STRUCTURE_TOGGLE}
				/>
			}
			footer={
				footerNote ? (
					<Text size="B6Medium" c="dark.0">
						{footerNote}
					</Text>
				) : null
			}
		/>
	);
};
